import React, { useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { changePassword } from '../../api/authApi';
import { Eyebrow, PageHeading, InlineAlert } from '../../design-system';
import styles from './ChangePasswordPage.module.css';

export const ChangePasswordPage: React.FC = () => {
  const { token } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }

    if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('New password must contain at least one letter and one number.');
      return;
    }

    if (newPassword.length > 72) {
      setError('New password must be at most 72 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    if (!token) {
      setError('You need to log in again before changing your password.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(token, { current_password: currentPassword, new_password: newPassword });
      setSuccess('Your password has been updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change password.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      <section className={styles.card}>
        <Eyebrow tone="default" dot>ACCOUNT SECURITY</Eyebrow>
        <PageHeading
          accentWord="password"
          accentTone="orange"
          size="h2"
          align="left"
          level="h1"
          className={styles.title}
        >
          Change password
        </PageHeading>
        <p className={styles.subtitle}>Update your password to keep your account secure.</p>
        {error && <InlineAlert variant="danger" title={error} />}
        {success && <InlineAlert variant="success" title={success} />}
        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.field}>
            <span className={styles.label}>Current password</span>
            <input
              type="password"
              className={styles.input}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>New password</span>
            <input
              type="password"
              className={styles.input}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Confirm new password</span>
            <input
              type="password"
              className={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </label>
          <button className={styles.submit} type="submit" disabled={submitting}>
            {submitting ? 'Updating password…' : 'Update password'}
          </button>
        </form>
      </section>
    </div>
  );
};
